import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Label } from '@/shared/ui/components/label';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { Forbidden } from '@/modules/auth/Forbidden';
import { apiErrorMessage } from '@/shared/api-client';
import {
  useCountryPolicies,
  useCountryReferenceData,
  useUpdateCountryPolicy,
  type CountryPolicy,
} from './countryPolicies';
import { CreateCountryPolicyDialog } from './CreateCountryPolicyDialog';

export function CountryPoliciesPage() {
  const { t } = useTranslation('common');
  const canManage = useAuthorization().can('franchise.country.manage');

  const { data, isLoading, isError } = useCountryPolicies();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CountryPolicy | null>(null);
  const [toggling, setToggling] = useState<CountryPolicy | null>(null);

  if (!canManage) return <Forbidden />;

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('countryPolicies.title')}</h1>
        <Button onClick={() => setCreating(true)}>{t('countryPolicies.actions.create')}</Button>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('countryPolicies.loading')}</p>}
      {isError && <p className="text-destructive">{t('countryPolicies.errors.loadFailed')}</p>}

      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('countryPolicies.columns.country')}</th>
                <th className="px-4 py-2 font-medium">{t('countryPolicies.columns.language')}</th>
                <th className="px-4 py-2 font-medium">{t('countryPolicies.columns.currency')}</th>
                <th className="px-4 py-2 font-medium">{t('countryPolicies.columns.timezone')}</th>
                <th className="px-4 py-2 font-medium">{t('countryPolicies.columns.status')}</th>
                <th className="px-4 py-2 font-medium">{t('countryPolicies.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    {t('countryPolicies.empty')}
                  </td>
                </tr>
              )}
              {data.items.map((policy) => (
                <tr key={policy.countryCode} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{policy.countryCode}</td>
                  <td className="px-4 py-2">{policy.defaultLanguage}</td>
                  <td className="px-4 py-2">{policy.defaultCurrency}</td>
                  <td className="px-4 py-2">{policy.defaultTimezone}</td>
                  <td className="px-4 py-2">
                    <span className={policy.enabled ? 'text-primary' : 'text-muted-foreground'}>
                      {policy.enabled
                        ? t('countryPolicies.status.enabled')
                        : t('countryPolicies.status.disabled')}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing(policy)}>
                        {t('countryPolicies.actions.edit')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setToggling(policy)}>
                        {policy.enabled
                          ? t('countryPolicies.actions.disable')
                          : t('countryPolicies.actions.enable')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateCountryPolicyDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => toast.success(t('countryPolicies.toast.created'))}
      />
      <EditCountryPolicyDialog
        policy={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => toast.success(t('countryPolicies.toast.updated'))}
      />
      <ToggleEnabledDialog
        policy={toggling}
        onOpenChange={(open) => !open && setToggling(null)}
        onToggled={(enabled) =>
          toast.success(
            enabled ? t('countryPolicies.toast.enabled') : t('countryPolicies.toast.disabled'),
          )
        }
      />
    </section>
  );
}

function EditCountryPolicyDialog({
  policy,
  onOpenChange,
  onSaved,
}: {
  policy: CountryPolicy | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('common');
  const open = policy !== null;
  const reference = useCountryReferenceData(open);
  const updatePolicy = useUpdateCountryPolicy();

  const [language, setLanguage] = useState('');
  const [currency, setCurrency] = useState('');
  const [timezone, setTimezone] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (policy) {
      setLanguage(policy.defaultLanguage);
      setCurrency(policy.defaultCurrency);
      setTimezone(policy.defaultTimezone);
      setError(null);
    }
  }, [policy]);

  if (!policy) return null;
  const currentPolicy = policy;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await updatePolicy.mutateAsync({
        countryCode: currentPolicy.countryCode,
        input: { defaultLanguage: language, defaultCurrency: currency, defaultTimezone: timezone },
      });
      onOpenChange(false);
      onSaved();
    } catch (caught) {
      setError(apiErrorMessage(t, 'countryPolicies', caught));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>
              {t('countryPolicies.edit.title', { code: policy.countryCode })}
            </DialogTitle>
            <DialogDescription>{t('countryPolicies.edit.description')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="cp-edit-language">{t('countryPolicies.fields.language')}</Label>
            <select
              id="cp-edit-language"
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
            >
              {(reference.data?.languages ?? [{ code: language, label: language }]).map(
                (option) => (
                  <option key={option.code} value={option.code}>
                    {option.label} ({option.code})
                  </option>
                ),
              )}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp-edit-currency">{t('countryPolicies.fields.currency')}</Label>
            <select
              id="cp-edit-currency"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
            >
              {(reference.data?.currencies ?? [{ code: currency, label: currency }]).map(
                (option) => (
                  <option key={option.code} value={option.code}>
                    {option.label} ({option.code})
                  </option>
                ),
              )}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp-edit-timezone">{t('countryPolicies.fields.timezone')}</Label>
            <select
              id="cp-edit-timezone"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
            >
              {(reference.data?.timezones ?? [timezone]).map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={updatePolicy.isPending}>
              {updatePolicy.isPending
                ? t('countryPolicies.edit.submitting')
                : t('countryPolicies.edit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToggleEnabledDialog({
  policy,
  onOpenChange,
  onToggled,
}: {
  policy: CountryPolicy | null;
  onOpenChange: (open: boolean) => void;
  onToggled: (enabled: boolean) => void;
}) {
  const { t } = useTranslation('common');
  const updatePolicy = useUpdateCountryPolicy();
  const open = policy !== null;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (policy) setError(null);
  }, [policy]);

  if (!policy) return null;
  const currentPolicy = policy;

  const nextEnabled = !currentPolicy.enabled;

  async function confirm() {
    setError(null);
    try {
      await updatePolicy.mutateAsync({
        countryCode: currentPolicy.countryCode,
        input: { enabled: nextEnabled },
      });
      onOpenChange(false);
      onToggled(nextEnabled);
    } catch (caught) {
      setError(apiErrorMessage(t, 'countryPolicies', caught));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {nextEnabled
              ? t('countryPolicies.confirm.enableTitle')
              : t('countryPolicies.confirm.disableTitle')}
          </DialogTitle>
          <DialogDescription>
            {nextEnabled
              ? t('countryPolicies.confirm.enable', { code: currentPolicy.countryCode })
              : t('countryPolicies.confirm.disable', { code: currentPolicy.countryCode })}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" disabled={updatePolicy.isPending} onClick={confirm}>
            {nextEnabled
              ? t('countryPolicies.actions.enable')
              : t('countryPolicies.actions.disable')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
